import {
  Breadcrumbs,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";

import { useEffect, useRef, useState } from "react";

import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ScopeHeader from "components/ScopeHeader";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DeletePopUp from "components/DeletePopup";
import i18n from "i18n/i18n";
import { useBoundStore } from "store/store";
import { countriesList } from "utils/countriesList";

function CreateSite() {
  const [siteName, setSiteName] = useState("");
  const [order, setOrder] = useState(0);
  const [street, setStreet] = useState("");
  const [address, setAddress] = useState("");

  const [platz, setPlatz] = useState("");
  const [ort, setOrt] = useState("");

  const [land, setLand] = useState("");
  const [sitenodeid, setsitenodeid] = useState("");

  const location = useLocation();

  const createSiteConfig = useBoundStore((state) => state.createSiteConfig);
  const updateSiteConfig = useBoundStore((state) => state.updateSiteConfig);

  const legalEntityNodeId = useBoundStore((state) => state.legalEntityNodeId);

  const isAuthUser = useBoundStore((state) => state.isAuthUser);
  const lang: any = useBoundStore((state) => state.lang);

  const loadTopology: any = useBoundStore((state) => state.loadTopology);

  const handleCreateSite = async () => {
    if (siteName === "") {
      siteNameRef.current.focus();
      return;
    }
    let obj = {
      lang: lang,
      name: siteName,
      city: ort,
      zipcode: platz,
      street: street,
      siteid: address,
      country: land,
      legalentitynodeid: legalEntityNodeId,
      order: order,
    };

    const promises = await createSiteConfig(obj);
    if (isAuthUser === true) {
      try {
        const result = await Promise.all(promises);
        navigate("/sites");
        await loadTopology(lang);
      } catch (error) {
        console.error("Error during promise execution:", error);
      }
    }
  };

  const handleEditSite = async () => {
    if (siteName === "") {
      siteNameRef.current.focus();
      return;
    }

    const promises = await updateSiteConfig(sitenodeid, {
      lang: lang,
      name: siteName,
      city: ort,
      zipcode: platz,
      street: street,
      siteid: address,
      country: land,
      legalentitynodeid: legalEntityNodeId,
      order: order,
    });

    if (isAuthUser === true) {
      try {
        const result = await Promise.all(promises);
        navigate("/sites?sitenodeid=" + sitenodeid);

        await loadTopology(lang);
      } catch (error) {
        console.error("Error during promise execution:", error);
      }
    }
  };

  const navigate = useNavigate();

  const getAllSites: any = useBoundStore((state) => state.getAllSites);
  const deleteSiteConfiguration: any = useBoundStore(
    (state) => state.deleteSiteConfiguration
  );

  useEffect(() => {
    if (location.state) {
      setSiteName(location.state.name);
      setStreet(location.state.street);
      setAddress(location.state.address);
      setPlatz(location.state.zipcode);
      setOrt(location?.state?.city);
      setLand(location.state.country);
      setsitenodeid(location.state.nodeid);
      setOrder(location.state.order);
    }
  }, [location.state]);

  const handleDelete = async () => {
    deleteSiteConfiguration({
      sitenodeid: location.state.nodeid,
      legalentitynodeid: legalEntityNodeId,
    });
    navigate("/sites");
    setTimeout(async () => {
      await loadTopology(lang);
    }, 2000);
  };

  const [openDialogDelete, setopenDialogDelete] = useState(false);

  const handleDialogDelete = () => {
    setopenDialogDelete(false);
  };

  const siteNameRef: any = useRef();
  const selectedCountries =
    countriesList[lang as "en" | "de"] || countriesList["de"]; // Fallback to English if lang is not found

  return (
    <>
      <ScopeHeader
        bgColor="#B21412"
        bgImage="images/scope_header.png"
        txtColor="#FFFFFF"
        title={i18n(lang)?.site_scope_title}
        description={i18n(lang)?.site_scope_description}
      />

      <Paper sx={{ padding: "24px" }}>
        <div className="createSite">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="_title">
              {location.state && location.state?.pageType === "edit"
                ? i18n(lang).edit_site_title
                : i18n(lang).create_site_title}
            </div>
            <div>
              {location.state && location.state?.pageType === "edit" ? (
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<DeleteForeverOutlinedIcon />}
                  onClick={() => {
                    setopenDialogDelete(true);
                  }}
                  sx={{
                    width: "100%",
                    color: "rgba(255, 61, 0, 1)",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: "4px",
                    background: "#ffffff",
                    ":hover": { color: "#ffffff" },
                    boxShadow: "none",
                  }}
                >
                  {i18n(lang).site} {i18n(lang).delete}
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>

          <Grid container spacing={3}>
            <Grid item xs={5}>
              <TextField
                sx={{ margin: "28px 0px 36px 0px" }}
                inputRef={siteNameRef}
                value={siteName}
                fullWidth
                required
                error={siteName === ""}
                onChange={(e) => {
                  setSiteName(e.target.value);
                }}
                label={i18n(lang).site_name}
                variant="outlined"
                helperText={i18n(lang).site_example}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                sx={{ margin: "28px 0px 36px 0px" }}
                value={order}
                type="number"
                fullWidth
                onChange={(e) => {
                  setOrder(parseInt(e.target.value, 10));
                }}
                label={i18n(lang).area_order_number}
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={5}>
              <TextField
                fullWidth
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                }}
                label={i18n(lang).street_house_number}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={5}>
              <TextField
                fullWidth
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                }}
                label={i18n(lang).additional_address}
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ marginTop: "4px" }}>
            <Grid item xs={1.5}>
              <TextField
                fullWidth
                value={platz}
                onChange={(e) => {
                  setPlatz(e.target.value);
                }}
                label={i18n(lang).postalcode}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={3.5}>
              <TextField
                fullWidth
                value={ort}
                onChange={(e) => {
                  setOrt(e.target.value);
                }}
                label={i18n(lang).location}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={5}>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">
                  {i18n(lang).land}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select-label"
                  value={land}
                  label={i18n(lang).land}
                  onChange={(e) => {
                    setLand(e.target.value);
                  }}
                >
                  {selectedCountries.map((object: any) => {
                    return (
                      <MenuItem value={object.code}>{object.country}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </Paper>

      <DeletePopUp
        open={openDialogDelete}
        handleClose={handleDialogDelete}
        handleDelete={handleDelete}
      ></DeletePopUp>

      <Button
        variant="contained"
        sx={{ marginTop: "20px" }}
        onClick={() => {
          location.state && location.state.pageType === "edit"
            ? handleEditSite()
            : handleCreateSite();
        }}
      >
        {" "}
        {location.state && location.state.pageType === "edit"
          ? i18n(lang).button_update_site
          : i18n(lang).button_create_site}
      </Button>
    </>
  );
}

export default CreateSite;
